import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DateParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import moment from 'moment';
import {
  Grid,
  MenuItem,
  Stack,
  TextField,
  Button,
  Box,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';

import { ProlongationStatuses } from '@/types';
import { AppBar, DebouncedTextField, Empty, Error, Loader } from '@/components';
import { CheckFlat, TimesFlat } from '@/assets/icons';
import { OrderDirections, ORDER_DIRECTIONS } from '@/utils/order-directions';
import { ProlongationsTable } from './components';
import {useLazyGetProlongationsQuery} from "@/services/prolongation";

export const PROLONGATION_STATUSES = [
  {
    label: 'Проведён',
    value: ProlongationStatuses.CARRIED_OUT,
    icon: <CheckFlat sx={{ fontSize: 16 }} color="success" />,
  },
  {
    label: 'Отменён',
    value: ProlongationStatuses.CANCELLED,
    icon: <TimesFlat sx={{ fontSize: 16 }} color="error" />,
  },
];

const ProlongationsPage: React.FC = () => {
  const { pledgeId } = useParams<{ pledgeId: string }>();

  const [
    { search, order_direction, page, date_from, date_to, status },
    setQuery,
  ] = useQueryParams({
    search: withDefault(StringParam, ''),
    page: withDefault(NumberParam, 1),
    order_direction: withDefault(StringParam, OrderDirections.DESC),
    status: withDefault(StringParam, ''),
    date_from: withDefault(
        DateParam,
        pledgeId ? null : moment().toDate()
    ),
    date_to: withDefault(DateParam, moment().toDate()),
  });

  // ленивый хук для запроса пролонгаций
  const [
    trigger,
    {
      data: prolongations,
      isLoading,
      isFetching,
      isError,
      isSuccess,
    },
  ] = useLazyGetProlongationsQuery();

  const [hasSearched, setHasSearched] = useState(false);

  // собираем параметры запроса
  const buildRequest = (overridePage = page) => ({
    params: {
      search: search || null,
      order_direction,
      page: overridePage,
      order_by: 'id',
      pledge_id: pledgeId ? +pledgeId : null,
      status,
      date_from: date_from
          ? moment(date_from).format('YYYY-MM-DD')
          : null,
      date_to: date_to
          ? moment(date_to).format('YYYY-MM-DD')
          : null,
    },
  });

  const onSearch = () => {
    setQuery({ page: 1 });
    setHasSearched(true);
    trigger(buildRequest(1));
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    trigger(buildRequest(newPage));
  };

  return (
      <>
        <AppBar title="Пролонгации" />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Grid container spacing={2}>
                {/* Поиск текста */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DebouncedTextField
                      type="search"
                      name="search"
                      label="Поиск"
                      placeholder="Введите значение"
                      value={search}
                      onChange={e =>
                          setQuery({ search: e.target.value, page: 1 })
                      }
                      variant="filled"
                      fullWidth
                      size="small"
                  />
                </Grid>

                {/* Сортировка */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={order_direction}
                      label="Сортировать по"
                      disabled={isLoading}
                      onChange={e =>
                          setQuery({ order_direction: e.target.value })
                      }
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    {ORDER_DIRECTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Статус */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={status}
                      label="Статус"
                      disabled={isLoading}
                      onChange={e =>
                          setQuery({ status: e.target.value })
                      }
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    <MenuItem value="">Все</MenuItem>
                    {PROLONGATION_STATUSES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Дата от */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата от"
                      value={date_from}
                      minDate={moment(date_to).subtract(6, 'months')}
                      maxDate={moment(date_to)}
                      onChange={val =>
                          setQuery({
                            date_from: val ? moment(val).toDate() : undefined,
                          })
                      }
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField
                              {...params}
                              variant="filled"
                              size="small"
                              fullWidth
                          />
                      )}
                  />
                </Grid>

                {/* Дата до */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата до"
                      value={date_to}
                      maxDate={moment()}
                      onChange={val =>
                          setQuery({
                            date_to: val ? moment(val).toDate() : undefined,
                          })
                      }
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField
                              {...params}
                              variant="filled"
                              size="small"
                              fullWidth
                          />
                      )}
                  />
                </Grid>

                {/* Кнопка «Поиск» */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <LoadingButton
                      loading={isFetching}
                      disabled={isFetching}
                      onClick={onSearch}
                      size="large"
                      variant="contained"
                      fullWidth
                  >
                    Поиск
                  </LoadingButton>
                </Grid>
              </Grid>
            </Stack>
          </Grid>

          {/* Результаты */}
          <Grid item xs={12}>
            {!hasSearched ? (
                <Empty text="Нажмите «Поиск», чтобы увидеть данные" />
            ) : isLoading ? (
                <Loader />
            ) : isError ? (
                <Error text="Произошла ошибка с загрузкой пролонгаций!" />
            ) : isSuccess && prolongations?.data?.length ? (
                <ProlongationsTable
                    list={prolongations}
                    page={page}
                    disabled={isFetching}
                    onChangePage={onChangePage}
                />
            ) : (
                <Empty text="Пролонгации отсутствуют" />
            )}
          </Grid>
        </Grid>
      </>
  );
};

export default ProlongationsPage;
